import DeleteOrgModal from "@web/components/DeleteOrgModal";
import TransferOwnershipModal from "@web/components/TransferOwnershipModal";
import { surfaceBase } from "@web/components/ui";
import { useEffect, useState } from "react";
import { useAuth, useToast } from "@/contexts";
import {
  checkOrgSlug,
  deleteOrg,
  getCurrentOrg,
  transferOrgOwnership,
  updateOrg,
} from "@/features/organizations/api";

export default function OrgSettings() {
  const { user, signOut } = useAuth();
  const toast = useToast();
  const [orgId, setOrgId] = useState<string | null>(null);
  const [orgName, setOrgName] = useState("");
  const [orgSlug, setOrgSlug] = useState("");
  const [originalName, setOriginalName] = useState("");
  const [originalSlug, setOriginalSlug] = useState("");
  const [role, setRole] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const [slugChecking, setSlugChecking] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data, error } = await getCurrentOrg();
      if (error) {
        toast.showAlert("Error", error.message, "error");
        return;
      }
      if (data) {
        setOrgId(data.id);
        setOrgName(data.name);
        setOrgSlug(data.slug);
        setOriginalName(data.name);
        setOriginalSlug(data.slug);
        setRole(data.role);
      }
    })();
  }, [user, toast]);

  useEffect(() => {
    if (orgSlug === originalSlug || orgSlug.length < 2) {
      setSlugAvailable(null);
      return;
    }
    setSlugChecking(true);
    const timer = setTimeout(async () => {
      const { data } = await checkOrgSlug(orgSlug);
      if (data) setSlugAvailable(data.available);
      setSlugChecking(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [orgSlug, originalSlug]);

  async function handleSave() {
    if (!orgId) return;
    setSaving(true);
    const { error } = await updateOrg(orgId, {
      name: orgName !== originalName ? orgName : undefined,
      slug: orgSlug !== originalSlug ? orgSlug : undefined,
    });
    if (error) {
      toast.showAlert("Error", error.message, "error");
    } else {
      setOriginalName(orgName);
      setOriginalSlug(orgSlug);
      toast.showAlert("Saved", "Organization settings updated.", "success");
    }
    setSaving(false);
  }

  async function handleDelete() {
    if (!orgId) return;
    const { error } = await deleteOrg(orgId);
    if (error) {
      toast.showAlert("Error", error.message, "error");
    } else {
      await signOut();
      window.location.href = "/";
    }
  }

  async function handleTransferOwnership(_targetUserId: string) {
    if (!orgId) return;
    const { error } = await transferOrgOwnership(orgId, _targetUserId);
    if (error) {
      toast.showAlert("Error", error.message, "error");
    } else {
      toast.showAlert("Transferred", "Organization ownership transferred.", "success");
      setRole("admin");
    }
  }

  const isOwner = role === "owner";
  const hasChanges = orgName !== originalName || orgSlug !== originalSlug;

  return (
    <div className="flex flex-col gap-4">
      <div className={`${surfaceBase} bg-card p-5`}>
        <span className="text-text text-[1.125rem] font-black tracking-[-0.0187em]">
          Organization Settings
        </span>
        <div className="flex flex-col gap-4 mt-4">
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="org-name"
              className="text-text-dim text-[0.75rem] font-bold uppercase tracking-[0.0313em]"
            >
              Organization Name
            </label>
            <input
              id="org-name"
              type="text"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              placeholder="Organization name"
              className="w-full rounded-[12px] border border-[rgba(28,27,46,0.2)] px-3 py-2.5 text-text text-[0.875rem] font-bold bg-white outline-none"
              disabled={!isOwner}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="org-slug"
              className="text-text-dim text-[0.75rem] font-bold uppercase tracking-[0.0313em]"
            >
              Organization Slug
            </label>
            <div className="relative">
              <input
                id="org-slug"
                type="text"
                value={orgSlug}
                onChange={(e) =>
                  setOrgSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""))
                }
                placeholder="org-slug"
                className="w-full rounded-[12px] border border-[rgba(28,27,46,0.2)] px-3 py-2.5 text-text text-[0.875rem] font-bold bg-white outline-none"
                disabled={!isOwner}
              />
              {slugChecking && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-text-dim text-xs">
                  Checking...
                </span>
              )}
              {slugAvailable === true && !slugChecking && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-aqua text-xs font-bold">
                  Available
                </span>
              )}
              {slugAvailable === false && !slugChecking && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#cc0000] text-xs font-bold">
                  Taken
                </span>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={handleSave}
            disabled={!hasChanges || saving || slugAvailable === false || !isOwner}
            className="bg-aqua rounded-[16px] py-3 px-5 text-white text-[0.875rem] font-extrabold border border-text self-start cursor-pointer disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save changes"}
          </button>
        </div>
      </div>

      {isOwner && (
        <div className={`${surfaceBase} bg-card p-5 border-[rgba(255,59,48,0.3)]`}>
          <span className="text-[#cc0000] text-[1.125rem] font-black tracking-[-0.0187em]">
            Danger Zone
          </span>
          <p className="text-text-muted text-[0.8125rem] mt-2 mb-4">
            Transfer ownership or delete this organization. These actions are irreversible.
          </p>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => setShowTransferModal(true)}
              className="bg-purple rounded-[16px] py-2.5 px-4 text-white text-[0.875rem] font-extrabold border border-text cursor-pointer self-start"
            >
              Transfer ownership
            </button>
            <button
              type="button"
              onClick={() => setShowDeleteModal(true)}
              className="bg-[#cc0000] rounded-[16px] py-2.5 px-4 text-white text-[0.875rem] font-extrabold border border-text cursor-pointer self-start"
            >
              Delete organization
            </button>
          </div>
        </div>
      )}

      {showDeleteModal && orgId && (
        <DeleteOrgModal
          orgName={orgName}
          onConfirm={handleDelete}
          onClose={() => setShowDeleteModal(false)}
        />
      )}

      {showTransferModal && orgId && user && (
        <TransferOwnershipModal
          orgId={orgId}
          currentOwnerId={user.id}
          onTransfer={handleTransferOwnership}
          onClose={() => setShowTransferModal(false)}
        />
      )}
    </div>
  );
}
